import React from "react";

interface FinancialStatsProps {
    stats:
        | {
              user_money: {
                  usd_total: number;
                  usdt_total: number;
              };
              investments?: Record<
                  string,
                  {
                      active: number;
                      pending: number;
                      withdraw_pending: number;
                      transfer_pending: number;
                  }
              >;
              guarantees: {
                  strategy_6_weekly: number;
                  strategy_6_total: number;
              };
          }
        | null
        | undefined;
    onConfirmWithdraw: () => void;
    onConfirmTransfer: () => void;
    isConfirmingWithdraw: boolean;
    isConfirmingTransfer: boolean;
}

export const FinancialStats: React.FC<FinancialStatsProps> = ({
    stats,
    onConfirmWithdraw,
    onConfirmTransfer,
    isConfirmingWithdraw,
    isConfirmingTransfer,
}) => {
    if (!stats) {
        return null;
    }

    // Вычисляем общую сумму pending выводов из всех стратегий
    const totalWithdrawPending =
        stats.investments
            ? Object.values(stats.investments).reduce(
                  (sum, strategy) =>
                      sum + (strategy.withdraw_pending || 0),
                  0
              )
            : 0;

    // Вычисляем сумму с учетом комиссии 1%
    const totalWithdrawAfterCommission = totalWithdrawPending * 0.99;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Деньги людей */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Деньги людей
                </h2>
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                            USD:
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">
                            ${stats.user_money.usd_total.toFixed(2)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                            USDT:
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">
                            ${stats.user_money.usdt_total.toFixed(2)}
                        </span>
                    </div>
                    {totalWithdrawPending > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Ожидает вывода:
                                </span>
                                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                    ${totalWithdrawPending.toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                    После комиссии 1%:
                                </span>
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    ${totalWithdrawAfterCommission.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={onConfirmWithdraw}
                        disabled={isConfirmingWithdraw || totalWithdrawPending === 0}
                        className={`w-full mt-2 px-4 py-2 rounded-md text-white font-medium transition-colors ${
                            totalWithdrawPending === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700"
                        } disabled:opacity-50`}
                    >
                        {isConfirmingWithdraw
                            ? "Выполнение..."
                            : totalWithdrawPending > 0
                            ? `Вывести все (${totalWithdrawAfterCommission.toFixed(2)} USDT)`
                            : "Нет средств для вывода"}
                    </button>
                </div>
            </div>

            {/* Гарантии 6-й стратегии */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Гарантии (Стратегия 6)
                </h2>
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                            Недельные:
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">
                            ${stats.guarantees.strategy_6_weekly.toFixed(2)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                            Всего:
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">
                            ${stats.guarantees.strategy_6_total.toFixed(2)}
                        </span>
                    </div>
                    <button
                        onClick={onConfirmTransfer}
                        disabled={isConfirmingTransfer}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {isConfirmingTransfer
                            ? "Выполнение..."
                            : "Перевести все"}
                    </button>
                </div>
            </div>
        </div>
    );
};
